/**
 * 롤백이 남긴 재확정 숙제를 `sessionStorage` 에 남긴다. 셸은 입고 탭을 벗어나면 패널을 언마운트하므로,
 * 탭 전환·새로고침 뒤에도 재확정 누락 경고와 "서버가 아직 롤백 중일 수 있음" 잠금이 풀리면 안 된다.
 * 읽기는 방어적이다 — 모양이 어긋난 값은 버리고 키를 지운다.
 */
export const GLOBAL_ROLLBACK_RECONFIRM_STORAGE_KEY = 'moms-console.global-rollback-reconfirm.v1'

export type ReconfirmStatus = {
  /** 롤백한 문서가 생긴 뒤 아직 재확정하지 않았는지. */
  reconfirmNeeded: boolean
  /** 응답 없이 끝난 실행에 담았던 문서. 서버가 아직 롤백 중일 수 있어 이 문서를 모두 다시 판정할 때까지 재확정을 막는다. */
  outcomeUnknownDocumentNumbers: string[]
}

export const EMPTY_RECONFIRM_STATUS: ReconfirmStatus = { reconfirmNeeded: false, outcomeUnknownDocumentNumbers: [] }

const isReconfirmStatus = (value: unknown): value is ReconfirmStatus => {
  if (!value || typeof value !== 'object') return false
  const status = value as Record<string, unknown>
  return (
    typeof status.reconfirmNeeded === 'boolean' &&
    Array.isArray(status.outcomeUnknownDocumentNumbers) &&
    status.outcomeUnknownDocumentNumbers.every((documentNumber) => typeof documentNumber === 'string')
  )
}

const clearStatus = (): void => {
  try {
    sessionStorage.removeItem(GLOBAL_ROLLBACK_RECONFIRM_STORAGE_KEY)
  } catch {
    /* 저장소를 못 써도 화면은 돌아야 한다 */
  }
}

const readStatus = (): unknown => {
  try {
    const raw = sessionStorage.getItem(GLOBAL_ROLLBACK_RECONFIRM_STORAGE_KEY)
    return raw ? JSON.parse(raw) : EMPTY_RECONFIRM_STATUS
  } catch {
    return null
  }
}

export const loadReconfirmStatus = (): ReconfirmStatus => {
  const stored = readStatus()
  if (isReconfirmStatus(stored)) return stored
  clearStatus()
  return EMPTY_RECONFIRM_STATUS
}

export const saveReconfirmStatus = (status: ReconfirmStatus): void => {
  try {
    sessionStorage.setItem(GLOBAL_ROLLBACK_RECONFIRM_STORAGE_KEY, JSON.stringify(status))
  } catch {
    /* 저장소를 못 써도 화면은 돌아야 한다 — 이번 마운트 동안은 화면 상태로 남아 있다 */
  }
}
